import type {RouteConfig} from './route.js';
import {
    combineCorsConfig,
    CorsConfig,
    CorsConfigSource, matchingCorsConfigSource,
    PERMIT_DEFAULT_CONFIG,
    validateCorsConfig
} from '../server/cors.js';
import {and, pattern, type ServerWebExchangeMatcher, upgradeMatcher} from '../server/util/matchers.js';
import {DefaultWebExchange} from '../server/exchange.js';
import {MockServerHttpRequest, MockServerHttpResponse} from '../mock/index.js';

function mockUpgradeExchange(path: string) {
    const request = new MockServerHttpRequest(path, 'GET');
    request.setHeader('Upgrade', 'websocket');
    request.upgrade = true
    return new DefaultWebExchange(request, new MockServerHttpResponse());
}

export async function createCorsConfigSource(context: RouteConfig): Promise<CorsConfigSource> {
    const {sockets: routes, cors} = context;

    // create a default CORS config that allows all origins and methods and then combine it with the provided corsConfig
    const defaultCorsConfig: CorsConfig = combineCorsConfig(PERMIT_DEFAULT_CONFIG, context.corsConfig);

    const validatedConfigs: Array<[ServerWebExchangeMatcher, CorsConfig | undefined]> = [];

    // for each socket route, combine the default CORS config, then any app CORS config that match, and finally the route CORS config
    for (const [path, route] of routes) {
        let routeCorsConfig: CorsConfig = defaultCorsConfig;
        const upgradeExchange = mockUpgradeExchange(path);
        for (const [matcher, config] of cors) {
            if ((await matcher(upgradeExchange)).match) {
                routeCorsConfig = combineCorsConfig(routeCorsConfig, config);
            }
        }
        routeCorsConfig = combineCorsConfig(routeCorsConfig, {
            allowOrigins: route.originFilters?.allow,
            allowMethods: ['GET', 'CONNECT'],
            allowHeaders: ['Upgrade', 'Connection', 'Origin', 'Sec-Websocket-Key', 'Sec-Websocket-Version', 'Sec-Websocket-Protocol', 'Sec-Websocket-Extensions'],
            exposeHeaders: ['Sec-Websocket-Accept', 'Sec-Websocket-Protocol', 'Sec-Websocket-Extensions'],
            allowCredentials: route.authorize?.access !== 'permitted',
        });
        validatedConfigs.push([and([upgradeMatcher, pattern(path)]), validateCorsConfig(routeCorsConfig)]);
    }

    // add app CORS configs
    for (const [matcher, config] of cors) {
        const routeCorsConfig = combineCorsConfig(defaultCorsConfig, config);
        validatedConfigs.push([matcher, validateCorsConfig(routeCorsConfig)]);
    }

    validatedConfigs.push([pattern(/\/api\/.*/), validateCorsConfig(defaultCorsConfig)]);


    return matchingCorsConfigSource({mappings: validatedConfigs});
}
